import React, { useRef, useState } from 'react'
import { useMediaQuery } from 'react-responsive';
import { MAX_FILE_SIZE, MOBILE_SCREEN_WIDTH } from '../constants/index';
import CustomToast from './CustomToast';
import { useSnackbar } from 'notistack';
import i18n from 'i18next';
import { getStableFile } from '../utils/util';

interface FileUploaderProps {
  onFileChange: (file:any) => void;
}

const FileUploader : React.FC<FileUploaderProps> = ({onFileChange}) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [isOver, setIsOver] = useState(false);
  const [error, setError] = useState<string|null>(null);
  // const { isLoading, talent } = useSelector((state: any) => state.talentMypage)
  const isMobile = useMediaQuery({ maxWidth: MOBILE_SCREEN_WIDTH });
  const fileRef = useRef<HTMLInputElement>(null);

  const showToast = (title: string, message: string, variant: string) => {
    enqueueSnackbar("I love snacks.", {
      content: (key) => (
        <CustomToast title={title} message={message} variant={variant} snackbarKey={key} closeSnackbar={closeSnackbar} />
      ),
    });
  };

  const handleChangeFile = async (e: any) => {
    if (e.target.files && e.target.files[0]) {
      const fileUpload = e.target.files[0];
      const isValid = validateFileUpload(fileUpload);
      if(isValid) {
        try {
          const stableFile = await getStableFile(fileUpload);
          onFileChange(stableFile);
        } catch (err) {
          console.error("Failed to clone file, falling back to original:", err);
          onFileChange(fileUpload);
        }
      }
    }
    e.target.value = '';
  };

  const handleDragOver = (e: any) => {
    e.preventDefault();
    setIsOver(true);
  };

  const handleDragLeave = (e: any) => {
    e.preventDefault();
    setIsOver(false);
  };

  const handleDrop = async (e: any) => {
    e.preventDefault();
    setIsOver(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      const isValid = validateFileUpload(droppedFile);
      if(isValid) {
        try {
          const stableFile = await getStableFile(droppedFile);
          onFileChange(stableFile);
        } catch (err) {
          console.error("Failed to clone file, falling back to original:", err);
          onFileChange(droppedFile);
        }
      }
    }
  };

  const validateFileUpload = (fileUpload: any) => {
    const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx'];
    const fileExtension = fileUpload.name.split('.').pop().toLowerCase();
    if (!allowedExtensions.includes(fileExtension)) {
      showToast('メッセージ', i18n.t('MSG_TALENT_UPLOAD_FILE_EXTENSION_INVALID'), 'error');
      return false;
    }
  
    if (fileUpload.size === 0) {
      showToast('メッセージ', i18n.t('MSG_TALENT_UPLOAD_FILE_EMPTY_OR_UNREADABLE'), 'error');
      return false;
    }

    if (fileUpload.size > MAX_FILE_SIZE.RESUME_FILE) {
      showToast('メッセージ', i18n.t('MSG_TALENT_UPLOAD_MAX_FILE_SIZE'), 'error');
      return false;
    }
    
    return true;
  };

  return (
    <div className="relative w-full h-[10rem]">
      <input
        accept=".pdf, .doc, .docx, .xls, .xlsx"
        onChange={handleChangeFile}
        type="file"
        className="hidden"
        ref={fileRef}
      />
      <div className="w-full h-full absolute top-0 left-0">
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileRef.current?.click()}
          className={`hover:cursor-pointer hover:bg-[#FFF3E5] hover:border-[#FF861A] w-full h-full ${
            isOver ? "bg-[#009CF2]" : "bg-white"
          } border border-dashed ${error ? "border-[#F33D62]" : "border-[#E0E0E0]"} flex flex-col gap-[0.625rem] items-center justify-center`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="49" height="56" viewBox="0 0 49 56" fill="none">
            <g clipPath="url(#clip0_6698_4579)">
              <path d="M40.4039 14H24.2423L20.2019 9.33331H8.08069C5.85847 9.33331 4.06049 11.4333 4.06049 14L4.04028 42C4.04028 44.5666 5.85847 46.6666 8.08069 46.6666H40.4039C42.6261 46.6666 44.4443 44.5666 44.4443 42V18.6666C44.4443 16.1 42.6261 14 40.4039 14ZM40.4039 42H8.08069V18.6666H40.4039V42ZM16.1615 30.3566L19.01 33.6466L22.2221 29.96V39.6666H26.2625V29.96L29.4746 33.67L32.3231 30.3566L24.2625 21L16.1615 30.3566Z" fill={isOver ? "#fff" : "#808080"}/>
            </g>
            <defs>
              <clipPath id="clip0_6698_4579">
                <rect width="48.4848" height="56" fill="white"/>
              </clipPath>
            </defs>
          </svg>
          
          <p
            className={`text-sm leading-[1.3125rem] ${
              isOver ? "text-white" : "alu-text-base-1"
            } text-center`}
          >
            {!isMobile ? "ファイルをドラッグ、またはクリックで選択" : "タップしてファイルを選択"}
          </p>
        </div>
        {error && (
          <p className="text-sm text-[#F33D62] mt-2">{error}</p>
        )}
      </div>
    </div>
  )
}

export default FileUploader